import type { Probe, Finding, ProbeResult } from '../engine/types';
import { ok, na } from '../engine/runner';
import { registerProbe } from '../engine/registry';
import { classifyGl } from '../data/webglRules';
import { rendererFamily, sig } from '../engine/normalize';
import { sortedFlags } from '../engine/normalize';

const HEADLESS_SOURCE = 'https://developer.chrome.org/blog/headless/';

/**
 * WebGL / GPU probe.
 *
 * The unmasked renderer string is the single most common automation tell:
 * real browsers run on GPU-backed ANGLE, while headless Chromium and VMs fall
 * back to SwiftShader or Mesa llvmpipe. Crucially, this is rarely patched even
 * by anti-detect browsers that shim userAgent and canvas.
 */
export const webglProbe: Probe = {
  id: 'webgl',
  label: 'WebGL / GPU',
  layer: 'gpu',
  tier: 'client',
  category: 'hardware',
  description: 'Unmasked GPU vendor/renderer, limits, and shader precision',
  budgetMs: 4000,

  async run(): Promise<ProbeResult> {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;

    const gl = (canvas.getContext('webgl2') ??
      canvas.getContext('webgl') ??
      canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;

    if (!gl) {
      return {
        signals: {
          available: na<boolean>('unavailable', 'client', 'no WebGL context'),
          renderer: na<string>('unavailable', 'client'),
          vendor: na<string>('unavailable', 'client'),
        },
      };
    }

    const started = performance.now();
    const findings: Finding[] = [];

    // --- unmasked vendor/renderer ----------------------------------------
    const dbg = gl.getExtension('WEBGL_debug_renderer_info');
    const unmaskedVendor = dbg
      ? String(gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL) ?? '')
      : null;
    const unmaskedRenderer = dbg
      ? String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) ?? '')
      : null;
    // Fall back to the masked values if the debug extension is unavailable.
    const vendor = unmaskedVendor ?? String(gl.getParameter(gl.VENDOR) ?? '');
    const renderer = unmaskedRenderer ?? String(gl.getParameter(gl.RENDERER) ?? '');

    // --- classification + software-rasterizer tell -----------------------
    const verdict = classifyGl(renderer);
    const family = rendererFamily(renderer);

    if (verdict?.value.class === 'software') {
      findings.push({
        id: 'webgl.software-rasterizer',
        severity: 'high',
        category: 'hardware',
        layer: 'gpu',
        confidence: 'high',
        title: `Software rasterizer detected: ${verdict.value.label}`,
        detail:
          'The WebGL renderer is a software rasterizer rather than a real GPU. Genuine desktop and mobile browsers use hardware-accelerated ANGLE backends; SwiftShader and llvmpipe appear mainly in headless browsers, VMs, and CI. This is the most common automation artifact, and anti-detect browsers frequently forget to patch it because canvas and WebGL shims target toDataURL, not the renderer string.',
        sources: [verdict.source, HEADLESS_SOURCE],
        evidence: [`renderer=${renderer}`, `class=software`],
        scoreContribution: 30,
      });
    }

    // HEADLESS in the UA is corroborating but covered by the general probe;
    // here we only note a Headless UA paired with a real GPU (odd but not illegal).

    // --- limits / precision ----------------------------------------------
    const maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number | null;
    const maxRenderbufferSize = gl.getParameter(gl.MAX_RENDERBUFFER_SIZE) as number | null;
    const maxVertexAttribs = gl.getParameter(gl.MAX_VERTEX_ATTRIBS) as number | null;
    const maxVaryingVectors = gl.getParameter(gl.MAX_VARYING_VECTORS) as number | null;
    const version = String(gl.getParameter(gl.VERSION) ?? '');
    const shadingLanguage = String(gl.getParameter(gl.SHADING_LANGUAGE_VERSION) ?? '');

    // Shader precision formats.
    const precision = {
      vertexHighFloat: getPrecision(gl, gl.VERTEX_SHADER, gl.HIGH_FLOAT),
      fragmentHighFloat: getPrecision(gl, gl.FRAGMENT_SHADER, gl.HIGH_FLOAT),
      fragmentMediumFloat: getPrecision(gl, gl.FRAGMENT_SHADER, gl.MEDIUM_FLOAT),
    };

    // Extensions (sorted so the part is stable).
    const extensions = sortedFlags(gl.getSupportedExtensions() ?? []);

    // Rendering probe: does a draw actually complete? Some headless configs
    // return a context that silently no-ops.
    const drawsOk = testDraw(gl);

    return {
      signals: {
        available: ok(true, 'client', performance.now() - started),
        hasDebugExtension: ok(!!dbg, 'client'),
        unmaskedVendor: ok(unmaskedVendor, 'client'),
        unmaskedRenderer: ok(unmaskedRenderer, 'client'),
        vendor: ok(vendor, 'client'),
        renderer: ok(renderer, 'client'),
        rendererClass: ok(verdict?.value.class ?? 'unknown', 'client'),
        rendererLabel: ok(verdict?.value.label ?? 'unclassified', 'client'),
        impliedOs: ok(verdict?.value.impliedOs ?? 'unknown', 'client'),
        version: ok(version, 'client'),
        shadingLanguageVersion: ok(shadingLanguage, 'client'),
        maxTextureSize: ok(maxTextureSize, 'client'),
        maxRenderbufferSize: ok(maxRenderbufferSize, 'client'),
        maxVertexAttribs: ok(maxVertexAttribs, 'client'),
        maxVaryingVectors: ok(maxVaryingVectors, 'client'),
        precision: ok(precision, 'client'),
        extensionCount: ok(extensions.length, 'client'),
        extensions: ok(extensions, 'client'),
        drawsOk: ok(drawsOk, 'client'),
      },
      findings,
      parts: {
        // Hash the coarse family + limits, not the full renderer string: driver
        // versions and ANGLE revisions churn constantly and would flip the part.
        gpu: {
          family,
          maxTextureSize: sig(maxTextureSize ?? 0),
          maxRenderbufferSize: sig(maxRenderbufferSize ?? 0),
          maxVertexAttribs,
          maxVaryingVectors,
          versionFamily: version.split(' ').slice(0, 2).join(' '),
        },
      },
    };
  },
};

function getPrecision(
  gl: WebGLRenderingContext,
  shaderType: number,
  precisionType: number,
): number | null {
  const fmt = gl.getShaderPrecisionFormat(shaderType, precisionType);
  if (!fmt) return null;
  return fmt.rangeMin === undefined ? null : fmt.rangeMax;
}

function testDraw(gl: WebGLRenderingContext): boolean {
  try {
    gl.clearColor(0, 0, 0, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
    const err = gl.getError();
    return err === gl.NO_ERROR;
  } catch {
    return false;
  }
}

registerProbe(webglProbe);
